import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert
} from '@mui/material';
import {
  People,
  Business,
  AccessTime,
  TrendingUp,
  CheckCircle,
  Schedule
} from '@mui/icons-material';
import api from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';

interface DashboardStats {
  totalUsers: number;
  totalOrganizations: number;
  totalTimesheets: number;
  activeSubscriptions: number;
  pendingApprovals: number;
  recentActivity: any[];
}

export default function DashboardV2() {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalOrganizations: 0,
    totalTimesheets: 0,
    activeSubscriptions: 0,
    pendingApprovals: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { formatDateTime } = useDateFormat();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load users
      const usersResponse = await api.getUsers();
      const totalUsers = usersResponse.data?.length || 0;

      // Load organizations
      const orgsResponse = await api.getOrganizations();
      const totalOrganizations = orgsResponse.data?.length || 0;

      // Load timesheet entries
      const timesheetsResponse = await api.getTimesheetEntries();
      const totalTimesheets = timesheetsResponse.data?.length || 0;

      // Load subscriptions (platform admin: all subscriptions; API returns { subscriptions })
      let activeSubscriptions = 0;
      try {
        const subscriptionsResponse = await api.getAllSubscriptions();
        const subs = subscriptionsResponse?.subscriptions || subscriptionsResponse?.data || [];
        activeSubscriptions = (Array.isArray(subs) ? subs : []).filter((sub: any) => sub.status === 'active').length;
      } catch (_) {
        // Non-fatal: show 0 active subscriptions
      }

      // Load pending approvals (API returns status; fallback to approvalStatus for compatibility)
      const pendingApprovals = timesheetsResponse.data?.filter((entry: any) => 
        (entry.status ?? entry.approvalStatus) === 'submitted'
      ).length || 0;

      // Mock recent activity for now
      const recentActivity = [
        { id: 1, action: 'User registered', user: 'john@example.com', timestamp: new Date().toISOString() },
        { id: 2, action: 'Timesheet submitted', user: 'jane@example.com', timestamp: new Date(Date.now() - 3600000).toISOString() },
        { id: 3, action: 'Organization created', user: 'admin@example.com', timestamp: new Date(Date.now() - 7200000).toISOString() },
      ];

      setStats({
        totalUsers,
        totalOrganizations,
        totalTimesheets,
        activeSubscriptions,
        pendingApprovals,
        recentActivity
      });
    } catch (err: any) {
      console.error('Error loading dashboard data:', err);
      setError(err.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        Dashboard
      </Typography>
      
      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <People color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Users</Typography>
              </Box>
              <Typography variant="h4" component="div">
                {stats.totalUsers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Registered users
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Business color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Organizations</Typography>
              </Box>
              <Typography variant="h4" component="div">
                {stats.totalOrganizations}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active organizations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccessTime color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Timesheets</Typography>
              </Box>
              <Typography variant="h4" component="div">
                {stats.totalTimesheets}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total entries
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Subscriptions</Typography>
              </Box>
              <Typography variant="h4" component="div">
                {stats.activeSubscriptions}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active subscriptions
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Approvals */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Pending Approvals</Typography>
              </Box>
              <Typography variant="h4" component="div" color="warning.main">
                {stats.pendingApprovals}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Timesheets awaiting approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* System Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">System Status</Typography>
              </Box>
              <Chip 
                label="All Systems Operational" 
                color="success" 
                icon={<CheckCircle />}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                All services running normally
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Action</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Timestamp</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.recentActivity.map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell>{activity.action}</TableCell>
                        <TableCell>{activity.user}</TableCell>
                        <TableCell>
                          {formatDateTime(activity.timestamp)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
